import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import AlertMessage from "../../../../components/AlertMessage";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import { SelectItemDto } from "../../../../types/SelectItemDto";
import { createOrganizationalStructure, deleteOrganizationalStructure, getOrganizationalStructureDetailById, getOrganizationalStructureTree, updateOrganizationalStructure } from "../api/organizationalStructureApi";
import { OrganizationalStructureDetailDto, OrganizationalStructureDto } from "../types";
import "./OrganizationalStructuresManagement.css";
import { getTranslatedDescription } from "../../../../utils/formatters/UserLanguageToDescriptionField";
import ModalGlobal from "../../../../components/AppModal/ModalGlobal";
import { useModal } from "../../../../hooks/useModal";
import { useUserPermissions } from "../../../../utils/auth";
import { PERMISSIONS } from "../../../../constants/permissions";
import { getOrganizationalStructureFormSchema } from "../schema/organizationalStructureSchema";
import OrganizationalStructuresTreePanel from "../components/OrganizationalStructuresTreePanel";
import OrganizationalStructureDetailForm from "../components/OrganizationalStructureDetailForm";

const getAllNodes = (nodes: OrganizationalStructureDto[]): OrganizationalStructureDto[] =>
    nodes.flatMap((node) => [node, ...getAllNodes(node.childrens || [])]);

const getFirstNodeId = (nodes: OrganizationalStructureDto[]): number | null => {
    if (!nodes.length) return null;
    return nodes[0].id;
};

export default function OrganizationalStructuresManagement() {
    const { t, i18n } = useTranslation(["OrganizationalStructure", "common"]);
    const queryClient = useQueryClient();
    const [selectedOrganizationalStructureId, setSelectedOrganizationalStructureId] = useState<number | null>(null);
    const [previousSelectedOrganizationalStructureId, setPreviousSelectedOrganizationalStructureId] = useState<number | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<{ [key: string]: string | undefined }>({});
    const [editableDetail, setEditableDetail] = useState<OrganizationalStructureDetailDto | null>(null);
    const { showModal: showDeleteModal, modalData: deleteModalData, openModal: openDeleteModal, closeModal: closeDeleteModal, isLoading: isDeleteLoading, setLoading: setDeleteLoading } = useModal<OrganizationalStructureDto>();
    const { showModal: showCannotDeleteModal, modalData: cannotDeleteModalData, closeModal: closeCannotDeleteModal } = useModal<OrganizationalStructureDto>();
    const permissions = useUserPermissions();
    
    const {
        data: OrganizationalStructureTree = [],
        isLoading: isLoadingTree,
        error: treeError,
    } = useQuery({
        queryKey: ["adminOrganizationalStructuresTree"],
        queryFn: getOrganizationalStructureTree,
    });

    const {
        data: OrganizationalStructureDetail,
        isLoading: isLoadingDetail,
        error: detailError,
    } = useQuery<OrganizationalStructureDetailDto>({
        queryKey: ["adminOrganizationalStructureDetail", selectedOrganizationalStructureId],
        queryFn: () => getOrganizationalStructureDetailById(selectedOrganizationalStructureId!),
        enabled: selectedOrganizationalStructureId != null,
    });

    useEffect(() => {
        if (!OrganizationalStructureTree.length) return;
        if (selectedOrganizationalStructureId == null) {
            setSelectedOrganizationalStructureId(getFirstNodeId(OrganizationalStructureTree));
        }
    }, [OrganizationalStructureTree]);

    const organizationalStructureOptions: SelectItemDto[] = useMemo(
        () =>
            getAllNodes(OrganizationalStructureTree).map((node) => ({
                id: node.id,
                name: `${node.code ? `${node.code} - ` : ""}${getTranslatedDescription(node.name, i18n.language)}`,
            })),
        [OrganizationalStructureTree, i18n.language]
    );

    const normalizeOrganizationalStructureDetail = (detail: OrganizationalStructureDetailDto): OrganizationalStructureDetailDto => ({
        ...detail,
        code: detail.code || "",
        name: {
            catalan: detail.name?.catalan || "",
            spanish: detail.name?.spanish || "",
            english: detail.name?.english || "",
        },
    });

    useEffect(() => {
        if (!OrganizationalStructureDetail) return;
        if (!isCreating) {
            setEditableDetail(normalizeOrganizationalStructureDetail(OrganizationalStructureDetail));
            setIsEditing(false);
            setValidationErrors({});
        }
    }, [OrganizationalStructureDetail, isCreating]);

    const createEmptyOrganizationalStructure = (): OrganizationalStructureDetailDto => ({
        id: 0,
        parentId: undefined,
        parentName: {
            catalan: "",
            spanish: "",
            english: "",
        },
        code: "",
        name: {
            catalan: "",
            spanish: "",
            english: "",
        }
    });

    const handleStartCreate = () => {
        setErrorMessage(null);
        setValidationErrors({});
        setPreviousSelectedOrganizationalStructureId(selectedOrganizationalStructureId);
        setSelectedOrganizationalStructureId(null);
        setIsCreating(true);
        setIsEditing(true);
        setEditableDetail(createEmptyOrganizationalStructure());
    };

    const handleCancel = () => {
        setErrorMessage(null);
        setIsSaving(false);
        setValidationErrors({});

        if (isCreating) {
            setIsCreating(false);
            setIsEditing(false);
            const restoredOrganizationalStructureId = previousSelectedOrganizationalStructureId ?? getFirstNodeId(OrganizationalStructureTree);
            setSelectedOrganizationalStructureId(restoredOrganizationalStructureId);
            return;
        }

        setEditableDetail(OrganizationalStructureDetail ? normalizeOrganizationalStructureDetail(OrganizationalStructureDetail) : null);
        setIsEditing(false);
    };

    const handleSave = async () => {
        if (!editableDetail || isSaving) return;

        const schema = getOrganizationalStructureFormSchema(t);
        setValidationErrors({});
        try {
            await schema.validate(editableDetail, { abortEarly: false });
        } catch (err: any) {
            const errors: { [key: string]: string | undefined } = {};
            if (Array.isArray(err?.inner) && err.inner.length > 0) {
                err.inner.forEach((error: any) => {
                    if (error.path) {
                        errors[error.path] = error.message;
                    }
                });
            } else if (err?.path) {
                errors[err.path] = err.message;
            }
            setValidationErrors(errors);
            return;
        }

        setErrorMessage(null);
        setIsSaving(true);

        try {
            const savedOrganizationalStructure = isCreating
                ? await createOrganizationalStructure(editableDetail)
                : await updateOrganizationalStructure(editableDetail.id, editableDetail);

            setEditableDetail(normalizeOrganizationalStructureDetail(savedOrganizationalStructure));
            setIsEditing(false);
            setIsCreating(false);
            showSuccessMessage(isCreating ? t("organizationalStructure:form.createSuccessfully") : t("organizationalStructure:form.updateSuccessfully"), 2000);

            await queryClient.invalidateQueries({ queryKey: ["adminOrganizationalStructuresTree"] });

            if (savedOrganizationalStructure?.id) {
                await queryClient.invalidateQueries({ queryKey: ["adminOrganizationalStructureDetail", savedOrganizationalStructure.id] });
                setSelectedOrganizationalStructureId(savedOrganizationalStructure.id);
            } else {
                await queryClient.invalidateQueries({ queryKey: ["adminOrganizationalStructureDetail"] });
            }
        } catch (error) {
            const apiError = error as ApiErrorResponse;
            setErrorMessage(apiError?.message ?? t("common:requestError.errorBadRequest"));
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = () => {
        openDeleteModal(
            null,
            t("organizationalStructure:modal.deleteTitle"),
            t("organizationalStructure:modal.deleteText", { name: (editableDetail ? getTranslatedDescription(editableDetail?.name, i18n.language) : "") }),
            "deleteWithoutInput",
            ""
        );
    }

    const confirmDelete = () => {
        setDeleteLoading(true);
        deleteOrganizationalStructure(selectedOrganizationalStructureId!)
            .then(async () => {
                showSuccessMessage(t("organizationalStructure:form.deleteSuccessfully"), 2000);
                setSelectedOrganizationalStructureId(previousSelectedOrganizationalStructureId);
                await queryClient.invalidateQueries({ queryKey: ["adminOrganizationalStructuresTree"] });
            })
            .catch((error) => {
                setErrorMessage((error as ApiErrorResponse)?.message ?? t("common:requestError.errorBadRequest"));
            }).finally(() => {
                setDeleteLoading(false);
                closeDeleteModal();
            });
    }

    const showSuccessMessage = (message: string, displayTime: number) => {
        setErrorMessage(null);
        setSuccessMessage(message);
        
        const timer = setTimeout(() => {
            setSuccessMessage(null);
        }, displayTime);

        return () => clearTimeout(timer);
    }

    return (
        <>
            <ModalGlobal
                show={showCannotDeleteModal}
                onClose={closeCannotDeleteModal}
                title={cannotDeleteModalData.title}
                contentType={cannotDeleteModalData.type}
                subtitle={cannotDeleteModalData.subtitle}
                closeText={t("common:buttons.buttonClose")}
                isLoading={false}
            />
            <ModalGlobal
                show={showDeleteModal}
                onClose={closeDeleteModal}
                title={deleteModalData.title}
                contentType={deleteModalData.type}
                subtitle={deleteModalData.subtitle}
                saveText={t("common:buttons.buttonAccept")}
                closeText={t("common:buttons.buttonCancel")}
                onSave={confirmDelete}
                isLoading={isDeleteLoading}
            />
            <Row className="mb-3 align-items-center">
                <Col xs={12} md={8}>
                    <h2>{t("organizationalStructure:managementTitle")}</h2>
                </Col>
                {permissions.length > 0 && permissions.includes(PERMISSIONS.ORGANIZATIONAL_STRUCTURE.CREATE) && (
                    <Col xs={12} md={4} className="text-end">
                        <Button
                            variant="primary"
                            className="px-4 rounded-pill"
                            onClick={handleStartCreate}
                            disabled={isEditing || isSaving}
                        >
                            {t("organizationalStructure:newOrganizationalStructure")}
                        </Button>
                    </Col>
                )}
            </Row>

            {(treeError || detailError || errorMessage) && (
                <Row>
                    <Col xs={12}>
                        <AlertMessage
                            message={
                                (treeError as ApiErrorResponse)?.message ||
                                (detailError as ApiErrorResponse)?.message ||
                                errorMessage ||
                                t("common:requestError.errorBadRequest")
                            }
                            type="danger"
                        />
                    </Col>
                </Row>
            )}
            {(successMessage) && (
                <Row>
                    <Col xs={12}>
                        <AlertMessage
                            message={successMessage}
                            type="success"
                        />
                    </Col>
                </Row>
            )}

            <Row>
                <Col xs={12} md={6}>
                    <OrganizationalStructuresTreePanel
                        isLoading={isLoadingTree}
                        nodes={OrganizationalStructureTree}
                        selectedOrganizationalStructureId={selectedOrganizationalStructureId}
                        onSelectOrganizationalStructure={setSelectedOrganizationalStructureId}
                        getNodeName={(node) => getTranslatedDescription(node.name, i18n.language)}
                    />
                </Col>

                <Col xs={12} md={6}>
                    <OrganizationalStructureDetailForm
                        isLoading={selectedOrganizationalStructureId != null ? isLoadingDetail : false}
                        editableDetail={editableDetail}
                        isEditing={isEditing}
                        isCreating={isCreating}
                        isSaving={isSaving}
                        errors={validationErrors}
                        setIsEditing={setIsEditing}
                        setEditableDetail={setEditableDetail}
                        setValidationErrors={setValidationErrors}
                        organizationalStructureOptions={organizationalStructureOptions}
                        onCancel={handleCancel}
                        onSave={handleSave}
                        onDelete={handleDelete}
                    />
                </Col>
            </Row>
        </>
    );
}
